/**
 * Portal Fetch — authenticated HTTP helper
 *
 * Wraps the native `fetch` with two behaviours:
 *  1. Automatically attaches the JWT from the auth store as `Authorization: Bearer`.
 *  2. On a 401 response, clears the auth session and resets the store so the app
 *     re-renders the login screen without requiring a manual page refresh.
 */

import { useAuthStore } from './authentication/auth-store';
import { authService } from './authentication/auth-service';

class SessionExpiredError extends Error {
  constructor() {
    super('Session expired — please log in again');
    this.name = 'SessionExpiredError';
  }
}

export async function portalFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = useAuthStore.getState().token;

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
    Authorization: `Bearer ${token ?? ''}`,
  };

  const response = await fetch(url, { ...options, headers });

  if (response.status === 401) {
    // Clears localStorage + resets the store, triggering app.tsx to unmount the
    // dashboard and render the login screen. The notice is set afterwards —
    // logout() resets the store, so setting it first would discard it — and it
    // is what tells the user why they were signed out mid-task.
    void authService.logout({ expired: true }).finally(() => {
      useAuthStore.getState().setNotice('session_expired');
    });
    throw new SessionExpiredError();
  }

  return response;
}
